import React from 'react';
import cx from 'classnames';
import { TrashIcon } from '@heroicons/react/24/outline';
import { INotaryDetailsData } from 'types/signingAppointment/INotaryDetailsData';
import { ISignerResponse } from 'types/signingAppointment/ISignerResponse';
import { Show } from '../../../../shared/Show';
import { TextFieldTW } from 'tailwindComponents/textInputTW/TextFieldTW';
import { PhoneNumberFieldTW } from 'tailwindComponents/phoneNumberFieldTW/PhoneNumberFieldTW';

export interface AddContactFormProps {
  isSigner: boolean;
  title: string;
  notary?: Omit<INotaryDetailsData, 'id'>;
  signer?: ISignerResponse;
  signerIndex?: number;
  showExistingContactForm?: boolean;
  existingContactNode?: React.ReactNode;
  hideTrashIcon?: boolean;
  onTrashIconClick: () => void;
  children?: React.ReactNode;
  columnsClassName?: string;
  emailRequired?: boolean;
  isLegacy?: boolean;
}

export const AddContactForm: React.FC<AddContactFormProps> = ({
  isSigner,
  title,
  notary,
  signer,
  signerIndex,
  showExistingContactForm,
  existingContactNode,
  hideTrashIcon = false,
  onTrashIconClick,
  children,
  columnsClassName,
  emailRequired = false,
  isLegacy = false,
}) => {
  return (
    <div data-test-id="add-contact-form-scheduling" className="mt-2 w-full pt-2">
      <section className="mb-4 flex items-center justify-between" data-test-id="contact-form-header">
        <span className="text-base font-semibold not-italic leading-6">{title}</span>
        <Show when={!hideTrashIcon}>
          <TrashIcon className="mr-1 h-5 w-5 cursor-pointer" data-test-id="trash-icon" onClick={onTrashIconClick} />
        </Show>
      </section>
      <Show when={!showExistingContactForm} fallback={existingContactNode}>
        <section className={cx('grid gap-4', columnsClassName ?? 'grid-cols-3')} data-test-id="new-user-form">
          <TextFieldTW
            labelText="First Name"
            name={isSigner ? `signers.${signerIndex}.firstName` : 'notary.firstName'}
            placeholder="Beth"
            value={isSigner ? signer?.firstName : notary?.firstName}
            isRequired={isSigner || isLegacy}
          />

          {isSigner && (
            <TextFieldTW
              labelText="Middle Name"
              name={`signers.${signerIndex}.middleName`}
              placeholder="B"
              value={signer?.middleName}
            />
          )}

          <TextFieldTW
            labelText="Last Name"
            name={isSigner ? `signers.${signerIndex}.lastName` : 'notary.lastName'}
            placeholder="Buyer"
            value={isSigner ? signer?.lastName : notary?.lastName}
            isRequired={isSigner || isLegacy}
          />

          <TextFieldTW
            labelText="Email Address"
            name={isSigner ? `signers.${signerIndex}.email` : 'notary.email'}
            placeholder="johnnylappleseed@gmail.com"
            value={isSigner ? signer?.email : notary?.email}
            isRequired={emailRequired}
          />

          <PhoneNumberFieldTW
            labelText="Phone Number"
            name={isSigner ? `signers.${signerIndex}.phoneNumber` : 'notary.phoneNumber'}
            value={isSigner ? signer?.phoneNumber : notary?.phoneNumber}
          />
        </section>
      </Show>
      {children}
    </div>
  );
};
